import React, { Component } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { connect } from "react-redux";
import Amplify, { Auth, Logger } from "aws-amplify";
import { Toast } from "aws-amplify-react/dist/Amplify-UI/Amplify-UI-Components-React";
import CustomAuthenticator from "./CustomAuthenticator";
import {
  updateAuthState, updateAuthData, updateProfile,
  deleteProfile, getUserPrivileges, setAmplifyConfigStatus
} from "../../redux/auth/actions";
import { resetReducers, resetStorage } from "../../redux/common/actions";
import { setShowToast } from "../../redux/settings/actions";
import { removePersistedStorageFromCognitoObject } from "../../helpers/amplifyHelperFunctions";
import App from "../../App";
import getHotelConfig from "../../getHotelConfig";

const logger = new Logger("CustomWithAuthenticator", "DEBUG");
const AUTHENTICATOR_AUTHSTATE = "amplify-authenticator-authState";

class CWA extends Component {
  constructor(props) {
    super(props);
    this._initialAuthState = props.authState || "signIn";
    this.includeGreetings = props.includeGreetings || false;
    this.authenticatorComponents = props.authenticatorComponents || [];
    this.federated = props.federated || null;
    this.theme = props.theme || null;
    this.signUpConfig = props.signUpConfig || null;

    this.handleAuthStateChange = this.handleAuthStateChange.bind(this);

    this.state = {
      errorPrivileges: props.error,
      modal: true
    };

    this.authConfig = {};

    if (typeof this.includeGreetings === "object" && this.includeGreetings !== null) {
      this.authConfig = Object.assign(this.authConfig, this.includeGreetings);
    } else {
      this.authConfig = {
        includeGreetings: this.includeGreetings,
        authenticatorComponents: this.authenticatorComponents,
        federated: this.federated,
        theme: this.theme,
        signUpConfig: this.signUpConfig
      };
    }
  }

  componentDidMount = () => {
    if (this.props.isSettingsLoaded && this.props.hotelConfig) {
      const amplifyConfig = this.configureAmplify(this.props.hotelConfig);
      if (amplifyConfig && Object.keys(amplifyConfig) && Object.keys(amplifyConfig).length) {
        this.props.setAmplifyConfigStatus(true);
      }
    }
    this._isMounted = true;
    this.checkUser();
    // TODO Clear toast on refresh
    // this.props.setShowToast(false);
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (
      prevProps.isSettingsLoaded !== this.props.isSettingsLoaded &&
      this.props.isSettingsLoaded &&
      this.props.hotelConfig &&
      Object.keys(this.props.hotelConfig).length &&
      !this.props.isAmplifyConfigured
    ) {
      const amplifyConfig = this.configureAmplify(this.props.hotelConfig);
      if (amplifyConfig && Object.keys(amplifyConfig) && Object.keys(amplifyConfig).length) {
        this.props.setAmplifyConfigStatus(true);
      }
    }
  };

  configureAmplify = config => {
    return Amplify.configure({
      Auth: {
        // REQUIRED - Amazon Cognito Identity Pool ID
        identityPoolId: config.awsCognitoIdentityPoolId,
        // REQUIRED - Amazon Cognito Region
        region: config.region || "eu-west-1",
        // OPTIONAL - Amazon Cognito User Pool ID
        userPoolId: config.awsCognitoUserPoolId,
        // OPTIONAL - Amazon Cognito Web Client ID
        userPoolWebClientId: config.awsCognitoUserPoolAppClientId,
        mandatorySignIn: true,
        authenticationFlowType: "USER_SRP_AUTH",
        aws_api_authenticationType: "AWS_IAM"
      },
      API: {
        endpoints: [
          {
            region: config.region || "eu-west-1",
            name: "WebeeAPI",
            endpoint: config.awsApiGatewayInvokeUrl,
            custom_header: async () => {
              return {
                Authorization: `${(await Auth.currentSession()).accessToken.jwtToken}`,
                "x-api-key": config.apiKey,
                redeem: config.redeem,
                Accept: "application/json",
                language: this.props.locale,
                "bundle-id": config.bundleId,
                "Content-Type": "application/json"
              };
            }
          },
          {
            region: config.region || "eu-west-1",
            name: "WebeePolicies",
            endpoint: config.awsApiGatewayInvokeUrl,
            custom_header: async () => {
              return {
                Authorization: `${(await Auth.currentSession()).accessToken.jwtToken}`,
                "identity-id": `${(await Auth.currentCredentials()).data.IdentityId}`,
                "x-api-key": config.apiKey,
                redeem: config.redeem,
                Accept: "application/json",
                "bundle-id": config.bundleId,
                "Content-Type": "application/json"
              };
            }
          },
          {
            region: config.region || "eu-west-1",
            name: "WebeeAuthorization",
            endpoint: config.awsApiGatewayInvokeUrl,
            custom_header: async () => {
              return {
                Authorization: `${(await Auth.currentSession()).accessToken.jwtToken}`,
                "x-api-key": config.AUTHORIZATION_API_KEY,
                redeem: config.redeem,
                Accept: "application/json",
                language: this.props.locale,
                "bundle-id": config.bundleId,
                "Content-Type": "application/json"
              };
            }
          }
        ]
      },
      Storage: {
        AWSS3: {
          bucket: config.bucket,
          region: config.region || "eu-west-1"
        }
      }
    });
  };

  buildUserObject = user => {
    const { attributes } = user;
    if (attributes) {
      const userProfileObject = {};
      for (let i = 0; i < Object.keys(attributes).length; i++) {
        const attributeName = Object.keys(attributes)[i];
        if (attributeName.indexOf("custom:") >= 0) {
          const name = attributeName.slice(7, attributeName.length);
          userProfileObject[name] = attributes[attributeName];
        } else {
          userProfileObject[attributeName] = attributes[attributeName];
        }
      }
      return userProfileObject;
    }
  };

  handleAuthStateChange = async (state, data) => {
    const { authState, updateAuthState, updateAuthData } = this.props;
    if (state === authState) {
      return;
    }

    try {
      localStorage.setItem(AUTHENTICATOR_AUTHSTATE, state);
    } catch (e) {
      logger.error("Failed to set the auth state into local storage", e);
    }

    updateAuthState(state);
    // RESET reducer for signin

    if (state === "signIn") {
      this.props.resetReducers();
      this.props.resetStorage();
    }

    if (state === "signedIn") {
      this.checkUser();
      await getHotelConfig("SIDEBAR");
    }
    if (data) {
      const cleanedData = removePersistedStorageFromCognitoObject(data);
      if (cleanedData) {
        updateAuthData(cleanedData);
      }
    }
  };

  checkUser() {
    Auth.currentAuthenticatedUser({ bypassCache: true })
      .then(user => {
        const profile = this.buildUserObject(user);
        this.setState({ profile: profile });
        this.props.updateProfile(profile);
      })
      .catch(err => {
        logger.error("err in amplify bridge checkuser ", err);
        this.props.updateAuthData({});
        this.props.deleteProfile();
        this.signOut()
      });
  }

  checkAuth = () => {
    // this.props.setShowToast(false)
    Auth.signOut()
      .then(() => {
        this.props.setShowToast(false);
      })
      .catch(err => {
        console.error(err);
      });
  };

  signOut = (data) => {
    let state = "signedOut";
    Auth.signOut()
      .then(() => {
        this.handleAuthStateChange(state, data);
        this.setState({ error: "Unauthorized user", showToast: true, profile: {} });
      })
      .catch(err => {
        this.handleAuthStateChange(state, data);
        logger.error(err);
      });
  }

  render() {
    const { authState, authData, hotelConfig, theme, showToast, error, isAuthorized, userPrivileges } = this.props;
    const signedIn = authState === "signedIn";
    let hotelId;

    if (hotelConfig && hotelConfig.hotelId) {
      hotelId = hotelConfig.hotelId;
    }

    if (signedIn && (isAuthorized === true || (userPrivileges !== null && Object.keys(userPrivileges).length > 0))) {
      return (
        <div>
          {showToast && (
            <Toast theme={theme} onClose={() => this.props.setShowToast(false)}>
              {typeof error === "string" || error instanceof String ? error : "Hotel config error"}
            </Toast>
          )}
          <React.Fragment>
            {this.authConfig.includeGreetings ? (
              <CustomAuthenticator
                {...this.props}
                appId="staff"
                /* TODO - appId should be dynamic */
                hotelId={hotelId}
                theme={this.authConfig.theme}
                federated={this.authConfig.federated || this.props.federated}
                hideDefault={this.authConfig.authenticatorComponents && this.authConfig.authenticatorComponents.length > 0}
                signUpConfig={this.authConfig.signUpConfig}
                usernameAttributes={this.authConfig.usernameAttributes}
                onStateChange={this.handleAuthStateChange}
                children={this.authConfig.authenticatorComponents || []}
              />
            ) : null}
            <App {...this.props} authState={authState} authData={authData} onStateChange={this.handleAuthStateChange} />
          </React.Fragment>
        </div>
      );
    }

    if (
      signedIn &&
      this.props.isAuthorized === false &&
      (this.props.internetConnected === null || this.props.internetConnected === true) &&
      !this.props.authError
    ) {
      this.props.setShowToast(true);

      return (
        <div>
          <div>
            <Modal isOpen={this.state.modal}>
              <ModalHeader>
                <i className="iconsminds-information" style={{ fontSize: "15px", color: "red" }} /> {/* <IntlMessages id="permission_error" /> */}
                Permission Error
              </ModalHeader>
              <ModalBody>
                This user does not have permission for this hotel
              </ModalBody>
              <ModalFooter>
                <div style={{ backgroundColor: "#922c88", borderRadius: "37px", color: "#fff" }}>
                  <Button onClick={() => this.checkAuth()}>Back</Button>
                </div>
              </ModalFooter>
            </Modal>
          </div>
        </div>
      );
    }

    if (signedIn && isAuthorized === false &&
      (this.props.internetConnected === null || this.props.internetConnected === true)) {
      this.signOut(this.state.profile);
      return (<></>);
    }

    return (
      <div>
        {showToast && (
          <Toast theme={theme} onClose={() => this.props.setShowToast(false)}>
            {typeof error === "string" || error instanceof String
              ? error
              : this.state.error
                ? this.state.error
                : "Hotel config error"}
          </Toast>
        )}

        <CustomAuthenticator
          {...this.props}
          appId="staff"
          /* TODO - appId should be dynamic */
          hotelId={hotelId}
          theme={this.authConfig.theme}
          federated={this.authConfig.federated || this.props.federated}
          hideDefault={this.authConfig.authenticatorComponents && this.authConfig.authenticatorComponents.length > 0}
          signUpConfig={this.authConfig.signUpConfig}
          usernameAttributes={this.authConfig.usernameAttributes}
          onStateChange={this.handleAuthStateChange}
          children={this.authConfig.authenticatorComponents || []}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ auth, settings, iot }) => {
  const { authState, authData, isUserPrivilegesLoading, isAmplifyConfigured, isAuthorized, userPrivileges } = auth;
  const authError = auth.error;
  const authLoading = auth.loading;
  const { hotelConfig, isSettingsLoaded, locale, showToast, error } = settings;
  const { internetConnected } = iot;
  return {
    authState,
    authData,
    hotelConfig,
    isSettingsLoaded,
    isAmplifyConfigured,
    authLoading,
    isUserPrivilegesLoading,
    locale,
    showToast,
    userPrivileges,
    error,
    isAuthorized,
    internetConnected,
    authError
  };
};
const mapActionsToProps = {
  updateAuthData,
  updateAuthState,
  updateProfile,
  deleteProfile,
  setShowToast,
  getUserPrivileges,
  setAmplifyConfigStatus,
  resetReducers,
  resetStorage
};
export default connect(
  mapStateToProps,
  mapActionsToProps
)(CWA);
